'use client'

import { useTranslations } from 'next-intl'

import type { ModelsNews } from '@/api/generated-api'
import { useNewsDetail } from '@/features/news/hooks'
import { useRouter } from '@/i18n/routing'
import Loader from '@/shared/ui/Loader'

import SalesBanner from '../components/SalesBanner'
import { isCtaEnabled } from '../utils/newsCtaFields'
import ArticleContent from './components/ArticleContent'
import HeroSection from './components/HeroSection'
import MoreInSports from './components/MoreInSports'
import NewsArticleCta from './components/NewsArticleCta'
import S from './NewsDetailPage.module.scss'

type NewsDetailClientProps = {
  id: string
  initialNews?: ModelsNews
}

export default function NewsDetailClient({
  id,
  initialNews
}: NewsDetailClientProps) {
  const t = useTranslations('news')
  const router = useRouter()
  const {
    data: refreshedNews,
    isLoading,
    error
  } = useNewsDetail(id, initialNews)
  const news = refreshedNews ?? initialNews

  if (!news && isLoading) {
    return (
      <div className={S.loading}>
        <Loader variant="fullscreen" />
      </div>
    )
  }

  if (error || !news) {
    return (
      <div className={S.error}>
        <p>{error ? t('page.error') : t('page.notFound')}</p>
      </div>
    )
  }

  return (
    <div className={S.newsDetailPage}>
      <HeroSection news={news} onGoBack={() => router.back()} />
      <ArticleContent news={news} />
      {isCtaEnabled(news) && <NewsArticleCta news={news} />}
      <MoreInSports excludeId={news.id} limit={3} />
      <SalesBanner />
    </div>
  )
}
